import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Star,
  Search,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Save,
  Send,
  XCircle,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Lock,
  LayoutList,
  Rows3,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { PageContainer } from "@/components/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import DeclineBatchModal from "@/components/questions/DeclineBatchModal";
import InlineField from "@/components/InlineField";
import InlineSelect from "@/components/InlineSelect";
import BatchListView, { RatingPicker } from "@/pages/BatchListView";
import { hasPerm, PERM } from "@/lib/permissions";
import { tagChipStyle, tagChipActiveStyle } from "@/lib/tagColors";

const OPTION_KEYS = [
  { k: "option_a", label: "A" },
  { k: "option_b", label: "B" },
  { k: "option_c", label: "C" },
  { k: "option_d", label: "D" },
];

const STATUS_BADGE = {
  unassigned: "bg-slate-100 text-slate-600",
  batched: "bg-indigo-50 text-indigo-700",
  assigned: "bg-blue-50 text-blue-700",
  saved: "bg-amber-50 text-amber-700",
  submitted: "bg-purple-50 text-purple-700",
  approved: "bg-emerald-50 text-emerald-700",
  rejected: "bg-rose-50 text-rose-700",
};

export default function BatchDetail() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [tags, setTags] = useState([]);
  const [lookups, setLookups] = useState({
    bloom_level: [],
    question_type: [],
    difficulty: [],
  });
  const [currentIdx, setCurrentIdx] = useState(0);
  const [rating, setRating] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [viewMode, setViewMode] = useState("single"); // "single" | "list"
  const [reviewSearch, setReviewSearch] = useState("");
  const [reviewStatus, setReviewStatus] = useState(""); // "" | assigned | re_review | saved | submitted | approved | rejected
  const [reviewPageSize, setReviewPageSize] = useState(20);
  const [reviewPage, setReviewPage] = useState(1);

  const isReviewer = user?.role === "sme_reviewer";
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  const loadBatch = useCallback(async () => {
    try {
      const [{ data: b }, { data: t }, { data: lk }] = await Promise.all([
        api.get(`/batches/${id}`),
        api.get("/tags"),
        api.get("/lookups"),
      ]);
      setBatch(b);
      setTags(t);
      setLookups(lk);
      // Load all questions for this batch, paginating in chunks if needed.
      // API page_size is capped server-side, so we walk pages until done.
      const PAGE_SIZE = 500;
      const all = [];
      let page = 1;
      while (true) {
        const { data: qd } = await api.get("/questions", {
          params: { batch_id: id, page_size: PAGE_SIZE, page },
        });
        all.push(...qd.items);
        if (all.length >= qd.total || qd.items.length === 0) break;
        page += 1;
      }
      setQuestions(all);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, [id]);

  useEffect(() => {
    loadBatch();
  }, [loadBatch]);

  // Filter (search + status) — applied to BOTH single and list views.
  const filteredQuestions = useMemo(() => {
    let arr = questions;
    if (reviewStatus) {
      arr = arr.filter((it) => {
        // "re_review" is a virtual status — assigned questions that have prior QC history.
        if (reviewStatus === "re_review") {
          return it.status === "assigned" && !!it.qc_at;
        }
        if (reviewStatus === "assigned") {
          // Pending review = assigned AND no prior QC (excludes re-reviews).
          return it.status === "assigned" && !it.qc_at;
        }
        return it.status === reviewStatus;
      });
    }
    if (!reviewSearch.trim()) return arr;
    const q = reviewSearch.trim().toLowerCase();
    return arr.filter((it) => {
      const hay = [
        it.source_id,
        it.question_text,
        it.subject,
        it.category,
        it.exam,
        it.bloom_level,
        it.difficulty,
        it.status,
        (it.tags || []).join(" "),
        it.reviewer_name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [questions, reviewSearch, reviewStatus]);

  // Pagination — only used by list view.
  const reviewPages = Math.max(1, Math.ceil(filteredQuestions.length / reviewPageSize));
  const pageStart = (reviewPage - 1) * reviewPageSize;
  const pagedQuestions = filteredQuestions.slice(pageStart, pageStart + reviewPageSize);

  useEffect(() => {
    // reset to page 1 whenever filter or page size changes
    setReviewPage(1);
  }, [reviewSearch, reviewStatus, reviewPageSize]);

  const current = filteredQuestions[currentIdx] || filteredQuestions[0] || null;

  // Sync editable review fields whenever the current question changes
  const currentId = current?.id;
  useEffect(() => {
    if (current) {
      setRating(current.rating || 0);
      setSelectedTags(current.tags || []);
      setNotes(current.notes || "");
    }
  }, [currentId, current]);

  const reviewedCount = useMemo(
    () =>
      questions.filter((q) =>
        ["submitted", "approved", "rejected"].includes(q.status)
      ).length,
    [questions]
  );

  // Reviewer can only edit when status is assigned/saved/unassigned (their draft)
  const isLockedForReviewer = current
    ? ["submitted", "approved", "rejected"].includes(current.status)
    : false;
  const canEdit = (!isReviewer && isAdmin) || (isReviewer && !isLockedForReviewer);

  const persist = async (endpoint, successMsg, advance) => {
    if (!current) return;
    if (rating < 1) return toast.error("Please add a rating (1–5)");
    setBusy(true);
    try {
      const { data } = await api.post(`/questions/${current.id}/${endpoint}`, {
        rating,
        tags: selectedTags,
        notes,
      });
      const updated = [...questions];
      updated[currentIdx] = data;
      setQuestions(updated);
      toast.success(successMsg);
      if (advance && currentIdx < questions.length - 1) {
        setCurrentIdx(currentIdx + 1);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = () => persist("save", "Draft saved", false);
  const submitFinal = () => persist("submit", "Submitted for QC", true);

  const qc = async (action) => {
    if (!current) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/questions/${current.id}/qc`, { action });
      const updated = [...questions];
      updated[currentIdx] = data;
      setQuestions(updated);
      toast.success(
        action === "approve" ? "Approved" : action === "reject" ? "Rejected" : "Sent back for re-review"
      );
      if (action === "approve" || action === "reject") {
        if (currentIdx < questions.length - 1) setCurrentIdx(currentIdx + 1);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!batch) {
    return (
      <PageContainer>
        <div className="text-center py-20 text-sm text-slate-400">Loading…</div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Header */}
      <button
        onClick={() => navigate("/batches")}
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Back to batches
      </button>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1
            className="text-2xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: "Outfit" }}
          >
            {batch.name}
          </h1>
          <div className="mt-1 text-xs text-slate-500 font-mono">
            Reviewer: {batch.reviewer_name || "—"} · {reviewedCount}/{batch.total_questions} submitted
            {batch.subject ? ` · ${batch.subject}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {/* View toggle */}
          <div className="mr-2 inline-flex items-center rounded-lg border border-slate-200 bg-white p-0.5">
            <button
              data-testid="view-mode-single"
              onClick={() => setViewMode("single")}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition ${
                viewMode === "single"
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              title="One question at a time"
            >
              <Rows3 className="w-3.5 h-3.5" /> Single
            </button>
            <button
              data-testid="view-mode-list"
              onClick={() => setViewMode("list")}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition ${
                viewMode === "list"
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:text-slate-900"
              }`}
              title="All questions in a list"
            >
              <LayoutList className="w-3.5 h-3.5" /> List
            </button>
          </div>

          {isReviewer && ["assigned", "in_review"].includes(batch.status) && (
            <button
              data-testid="decline-batch-header-btn"
              onClick={() => setDeclineOpen(true)}
              className="mr-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-50 text-amber-700 hover:bg-amber-100"
            >
              <XCircle className="w-3.5 h-3.5" /> Decline batch
            </button>
          )}
          {viewMode === "single" && (
            <>
              <button
                onClick={() => setCurrentIdx((i) => Math.max(0, i - 1))}
                disabled={currentIdx === 0}
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="px-3 py-2 text-sm font-mono text-slate-600">
                {Math.min(currentIdx + 1, filteredQuestions.length)} / {filteredQuestions.length}
              </div>
              <button
                onClick={() =>
                  setCurrentIdx((i) => Math.min(filteredQuestions.length - 1, i + 1))
                }
                disabled={currentIdx >= filteredQuestions.length - 1}
                className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50 disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Review toolbar — search + status + (in list view) page-size + pager */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="review-search-input"
            type="text"
            placeholder="Search by ID, question text, subject, tag…"
            value={reviewSearch}
            onChange={(e) => setReviewSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
          />
        </div>
        <select
          data-testid="review-status-filter"
          value={reviewStatus}
          onChange={(e) => setReviewStatus(e.target.value)}
          className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        >
          <option value="">All statuses</option>
          <option value="assigned">Pending review</option>
          <option value="re_review">Sent back for re-review</option>
          <option value="saved">Saved drafts</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
        {viewMode === "list" && (
          <>
            <label className="inline-flex items-center gap-2 text-xs font-mono text-slate-500">
              Per page
              <select
                data-testid="review-page-size"
                value={reviewPageSize}
                onChange={(e) => setReviewPageSize(Number(e.target.value))}
                className="px-2 py-1.5 rounded-md border border-slate-200 text-sm bg-white outline-none focus:border-orange-500"
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <div className="ml-auto flex items-center gap-2 text-xs font-mono text-slate-500">
              <button
                onClick={() => setReviewPage((p) => Math.max(1, p - 1))}
                disabled={reviewPage === 1}
                className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span>
                Page {reviewPage} of {reviewPages} · {filteredQuestions.length} matches
              </span>
              <button
                onClick={() => setReviewPage((p) => Math.min(reviewPages, p + 1))}
                disabled={reviewPage === reviewPages}
                className="p-1.5 rounded-md hover:bg-slate-100 disabled:opacity-30"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
        {viewMode === "single" && reviewSearch && (
          <div className="ml-auto text-xs font-mono text-slate-500">
            {filteredQuestions.length} match{filteredQuestions.length === 1 ? "" : "es"}
          </div>
        )}
      </div>

      {viewMode === "list" ? (
        <BatchListView
          questions={pagedQuestions}
          tags={tags}
          lookups={lookups}
          user={user}
          onRefresh={loadBatch}
          onUpdate={(idxInPage, updated) => {
            // idxInPage is relative to pagedQuestions — translate to full questions array
            const target = pagedQuestions[idxInPage];
            const full = questions.findIndex((x) => x.id === target.id);
            if (full < 0) return;
            const next = [...questions];
            next[full] = updated;
            setQuestions(next);
          }}
        />
      ) : !current ? (
        <div className="bg-white rounded-2xl border border-slate-100 brand-shadow p-12 text-center text-sm text-slate-400">
          {reviewSearch
            ? "No questions match this search."
            : "No questions in this batch."}
        </div>
      ) : (
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        {/* Question */}
        <div className="bg-white rounded-2xl border border-slate-100 brand-shadow p-6">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <span className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-slate-100 text-slate-600">
              ID {current.source_id || "—"}
            </span>
            <span
              className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-mono uppercase tracking-wider ${
                STATUS_BADGE[current.status] || "bg-slate-100 text-slate-600"
              }`}
              data-testid="question-status-badge"
            >
              {current.status === "approved" && <CheckCircle2 className="w-3 h-3" />}
              {current.status === "rejected" && <XCircle className="w-3 h-3" />}
              {current.status === "submitted" && <Lock className="w-3 h-3" />}
              {current.status}
            </span>
            {hasPerm(user, PERM.QUESTIONS_EDIT) && (
              <span className="text-[10px] font-mono uppercase tracking-wider text-orange-600">
                Fields editable
              </span>
            )}
          </div>

          {/* Inline-editable metadata grid */}
          {hasPerm(user, PERM.QUESTIONS_EDIT) && (
            <div className="mb-5 grid grid-cols-2 md:grid-cols-4 gap-x-3 gap-y-2 text-sm">
              <SingleField label="Source ID">
                <InlineField
                  questionId={current.id}
                  field="source_id"
                  value={current.source_id}
                  onSaved={loadBatch}
                  className="font-mono text-xs"
                />
              </SingleField>
              <SingleField label="Exam" locked>
                <span className="text-sm text-slate-700">{current.exam || "—"}</span>
              </SingleField>
              <SingleField label="Subject" locked>
                <span className="text-sm text-slate-700">{current.subject || "—"}</span>
              </SingleField>
              <SingleField label="Category" locked>
                <span className="text-sm text-slate-700">{current.category || "—"}</span>
              </SingleField>
              <SingleField label="Bloom Level">
                <InlineSelect
                  questionId={current.id}
                  field="bloom_level"
                  value={current.bloom_level}
                  options={lookups.bloom_level}
                  onSaved={loadBatch}
                />
              </SingleField>
              <SingleField label="Question Type">
                <InlineSelect
                  questionId={current.id}
                  field="question_type"
                  value={current.question_type}
                  options={lookups.question_type}
                  onSaved={loadBatch}
                />
              </SingleField>
              <SingleField label="Difficulty">
                <InlineSelect
                  questionId={current.id}
                  field="difficulty"
                  value={current.difficulty}
                  options={lookups.difficulty}
                  onSaved={loadBatch}
                />
              </SingleField>
              <SingleField label="Correct">
                <InlineField
                  questionId={current.id}
                  field="correct_answer"
                  value={current.correct_answer}
                  onSaved={loadBatch}
                  className="font-mono text-xs font-semibold uppercase text-emerald-700"
                />
              </SingleField>
              <SingleField label="Time (s)">
                <InlineField
                  questionId={current.id}
                  field="time"
                  value={current.time}
                  numeric
                  onSaved={loadBatch}
                  className="font-mono text-xs"
                />
              </SingleField>
              <SingleField label="Mark">
                <InlineField
                  questionId={current.id}
                  field="mark"
                  value={current.mark}
                  numeric
                  onSaved={loadBatch}
                  className="font-mono text-xs"
                />
              </SingleField>
            </div>
          )}

          {!hasPerm(user, PERM.QUESTIONS_EDIT) && (
            <div className="mb-5 flex flex-wrap items-center gap-2">
              {current.exam && (
                <span className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-slate-100 text-slate-600">
                  {current.exam}
                </span>
              )}
              {current.subject && (
                <span className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-slate-100 text-slate-600">
                  {current.subject}
                </span>
              )}
              {current.category && (
                <span className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-orange-50 text-orange-700">
                  {current.category}
                </span>
              )}
              {current.bloom_level && (
                <span className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-slate-100 text-slate-600">
                  Bloom: {current.bloom_level}
                </span>
              )}
              {current.difficulty && (
                <span className="px-2 py-0.5 rounded-md text-[11px] font-mono bg-slate-100 text-slate-600">
                  {current.difficulty}
                </span>
              )}
            </div>
          )}

          {hasPerm(user, PERM.QUESTIONS_EDIT) ? (
            <div className="mb-6">
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">
                Question
              </div>
              <InlineField
                questionId={current.id}
                field="question_text"
                value={current.question_text}
                multiline
                onSaved={loadBatch}
                className="text-base font-semibold text-slate-900"
              />
            </div>
          ) : (
            <p className="text-lg text-slate-900 leading-relaxed mb-6">{current.question_text}</p>
          )}

          <div className="space-y-2.5 mb-6">
            {OPTION_KEYS.map(({ k, label }) => {
              const isCorrect =
                current.correct_answer &&
                current.correct_answer.toString().toUpperCase().includes(label);
              return (
                <div
                  key={k}
                  className={`flex items-start gap-3 p-3.5 rounded-lg border ${
                    isCorrect
                      ? "border-emerald-200 bg-emerald-50/50"
                      : "border-slate-200 bg-slate-50/30"
                  }`}
                >
                  <span
                    className={`w-7 h-7 shrink-0 rounded-md font-mono text-sm font-bold flex items-center justify-center ${
                      isCorrect
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-white text-slate-600 border border-slate-200"
                    }`}
                  >
                    {label}
                  </span>
                  <div className="flex-1 min-w-0">
                    {hasPerm(user, PERM.QUESTIONS_EDIT) ? (
                      <InlineField
                        questionId={current.id}
                        field={k}
                        value={current[k]}
                        onSaved={loadBatch}
                        className="text-sm text-slate-800"
                      />
                    ) : (
                      <span className="text-sm text-slate-800">{current[k] || "—"}</span>
                    )}
                  </div>
                  {isCorrect && (
                    <span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 font-semibold shrink-0">
                      Correct
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-lg bg-amber-50/50 border border-amber-100 p-4">
            <div className="text-[11px] font-mono uppercase tracking-wider text-amber-700 mb-1">
              Rationale
            </div>
            {hasPerm(user, PERM.QUESTIONS_EDIT) ? (
              <InlineField
                questionId={current.id}
                field="rationale"
                value={current.rationale}
                multiline
                onSaved={loadBatch}
                className="text-sm text-slate-700"
              />
            ) : (
              <p className="text-sm text-slate-700 leading-relaxed">{current.rationale || "—"}</p>
            )}
          </div>

          {current.reviewer_name && (
            <div className="mt-4 p-3 rounded-lg bg-slate-50 text-xs text-slate-600 font-mono">
              Reviewed by <strong>{current.reviewer_name}</strong> on{" "}
              {current.review_date ? new Date(current.review_date).toLocaleString() : "—"}
              {current.qc_by && (
                <>
                  <br />
                  QC by <strong>{current.qc_by}</strong> ({current.qc_status}) on{" "}
                  {current.qc_at ? new Date(current.qc_at).toLocaleString() : "—"}
                </>
              )}
            </div>
          )}
        </div>

        {/* Review sidebar */}
        <aside className="bg-white rounded-2xl border border-slate-100 brand-shadow p-6 h-fit lg:sticky lg:top-6">
          <h3 className="font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>
            Review
          </h3>

          {isLockedForReviewer && isReviewer && (
            <div className="mb-4 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-600 flex items-start gap-2">
              <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <div>
                This question is <strong>{current.status}</strong>. Contact the admin to reopen if
                changes are needed.
              </div>
            </div>
          )}

          {/* Rating */}
          <div className="mb-5">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2 flex items-center justify-between">
              <span>Quality Rating</span>
              {current.raw_rating != null && (
                <span
                  data-testid="single-raw-rating"
                  title="Raw rating from the uploaded sheet"
                  className="px-1.5 py-0.5 rounded-md text-[10px] font-mono normal-case bg-slate-100 text-slate-600"
                >
                  Raw: {current.raw_rating}
                </span>
              )}
            </div>
            <RatingPicker
              value={rating}
              onChange={setRating}
              disabled={!canEdit}
              testIdPrefix="review-rating-input"
            />
          </div>

          {/* Tags — single select, color-coded */}
          <div className="mb-5" data-testid="review-tag-selector">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
              Tag <span className="normal-case tracking-normal text-slate-400">(pick one)</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {tags.length === 0 && (
                <div className="text-xs text-slate-400">No published tags yet.</div>
              )}
              {tags.map((t) => {
                const on = selectedTags[0] === t.name;
                return (
                  <button
                    key={t.id}
                    type="button"
                    disabled={!canEdit}
                    onClick={() => setSelectedTags(on ? [] : [t.name])}
                    style={on ? tagChipActiveStyle(t.color) : tagChipStyle(t.color)}
                    className="px-2.5 py-1 rounded-md text-xs font-mono border transition disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid={`review-tag-${t.name.replace(/\s+/g, "-")}`}
                  >
                    {t.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-5">
            <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-2">
              Notes
            </div>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              disabled={!canEdit}
              placeholder="Add review notes…"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none disabled:bg-slate-50 disabled:text-slate-500"
              data-testid="review-notes-input"
            />
          </div>

          {/* Reviewer action buttons */}
          {isReviewer && !isLockedForReviewer && (
            <div className="space-y-2">
              <button
                data-testid="save-draft-btn"
                onClick={saveDraft}
                disabled={busy || rating < 1}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white border border-slate-200 hover:border-orange-300 hover:bg-orange-50 disabled:opacity-40 disabled:cursor-not-allowed text-slate-700 text-sm font-semibold transition"
              >
                <Save className="w-4 h-4" />
                {busy ? "Saving…" : "Save Draft"}
              </button>
              <button
                data-testid="submit-review-btn"
                onClick={submitFinal}
                disabled={busy || rating < 1}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:bg-slate-200 disabled:text-slate-400 text-white text-sm font-semibold transition"
              >
                <Send className="w-4 h-4" />
                {busy ? "Submitting…" : "Submit Final"}
              </button>
            </div>
          )}

          {/* Admin QC actions */}
          {isAdmin && current.status === "submitted" && (
            <div className="space-y-2 pt-3 border-t border-slate-100">
              <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
                Quality Control
              </div>
              <button
                data-testid="qc-approve-btn"
                onClick={() => qc("approve")}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                <ThumbsUp className="w-4 h-4" /> Approve
              </button>
              <button
                data-testid="qc-reject-btn"
                onClick={() => qc("reject")}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                <ThumbsDown className="w-4 h-4" /> Reject
              </button>
              <button
                data-testid="qc-resend-btn"
                onClick={() => qc("resend")}
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-white border border-amber-200 text-amber-700 hover:bg-amber-50 text-sm font-semibold disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" /> Send Back for Re-review
              </button>
            </div>
          )}

          {!isReviewer && !isAdmin && (
            <div className="text-xs text-slate-400 text-center">View-only access</div>
          )}
        </aside>
      </div>
      )}

      {declineOpen && (
        <DeclineBatchModal
          batch={batch}
          onClose={() => setDeclineOpen(false)}
          onDeclined={() => {
            setDeclineOpen(false);
            navigate("/batches");
          }}
        />
      )}
    </PageContainer>
  );
}

function SingleField({ label, locked = false, children }) {
  return (
    <div className="flex flex-col">
      <span className="text-[9px] font-mono uppercase tracking-wider text-slate-400 inline-flex items-center gap-1">
        {label}
        {locked && (
          <svg viewBox="0 0 24 24" fill="none" className="w-2.5 h-2.5">
            <path d="M7 10V7a5 5 0 0110 0v3M5 10h14v10H5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {children}
    </div>
  );
}
